import { canvasToJpegBytes, downloadBlob, jpegToPdfBytes } from './jpegPdf';
import { holesOnSection, sectionPlotTitle, formatMine, gradeLegendRows } from './sectionPlot';
import { getLiveGradeThresholds } from './gradePalette';

function niceStep(span, target = 8) {
  if (!(span > 0)) return 10;
  const raw = span / target;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const n = raw / pow;
  const step = n < 1.5 ? 1 : n < 3.5 ? 2 : n < 7.5 ? 5 : 10;
  return step * pow;
}

function drawNorth(ctx, x, y, scale) {
  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = '#eab308';
  ctx.beginPath();
  ctx.moveTo(0, -16 * scale);
  ctx.lineTo(7 * scale, 8 * scale);
  ctx.lineTo(0, 3 * scale);
  ctx.lineTo(-7 * scale, 8 * scale);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = '#facc15';
  ctx.font = `bold ${11 * scale}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('N', x, y + 28 * scale);
  ctx.textAlign = 'start';
}

/**
 * Draw a 2D section plot (holes projected onto the current plane).
 * Returns the canvas.
 */
export function renderSectionPlotCanvas(state, {
  projectName,
  thresholds,
  width = 1600,
  height = 1000,
  intervalMode = 'assay',
  desurvey,
} = {}) {
  const palette = thresholds || getLiveGradeThresholds();
  const { basis, holes, bounds } = holesOnSection(state, {
    thresholds: palette,
    intervalMode,
    desurvey,
  });
  const title = sectionPlotTitle(state, projectName);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#141414';
  ctx.fillRect(0, 0, width, height);

  const padL = 88;
  const padR = 160;
  const padT = 72;
  const padB = 88;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;

  let minU = bounds?.minU ?? -50;
  let maxU = bounds?.maxU ?? 50;
  let minV = bounds?.minV ?? -50;
  let maxV = bounds?.maxV ?? 50;
  const spanU = Math.max(20, maxU - minU);
  const spanV = Math.max(20, maxV - minV);
  minU -= spanU * 0.08;
  maxU += spanU * 0.08;
  minV -= spanV * 0.08;
  maxV += spanV * 0.08;
  const worldW = maxU - minU;
  const worldH = maxV - minV;
  const scale = Math.min(plotW / worldW, plotH / worldH);
  const ox = padL + (plotW - worldW * scale) / 2;
  const oy = padT + (plotH - worldH * scale) / 2;
  const toX = (u) => ox + (u - minU) * scale;
  const mapY = (v) => padT + (oy - padT) + (maxV - v) * scale;

  ctx.fillStyle = '#0c0c0c';
  ctx.fillRect(padL, padT, plotW, plotH);
  ctx.strokeStyle = '#eab308';
  ctx.lineWidth = 1.2;
  ctx.strokeRect(padL, padT, plotW, plotH);

  const stepU = niceStep(worldW);
  const stepV = niceStep(worldH);
  ctx.font = '11px sans-serif';
  ctx.fillStyle = '#a1a1aa';
  ctx.strokeStyle = '#27272a';
  ctx.lineWidth = 1;
  const startU = Math.ceil(minU / stepU) * stepU;
  for (let u = startU; u <= maxU + 1e-6; u += stepU) {
    const x = toX(u);
    ctx.beginPath();
    ctx.moveTo(x, padT);
    ctx.lineTo(x, padT + plotH);
    ctx.stroke();
    ctx.fillStyle = '#d4d4d8';
    ctx.textAlign = 'center';
    ctx.fillText(formatMine(u + (basis.isPlan ? basis.center.east : 0), 0), x, padT + plotH + 18);
  }
  const startV = Math.ceil(minV / stepV) * stepV;
  for (let v = startV; v <= maxV + 1e-6; v += stepV) {
    const y = mapY(v);
    ctx.beginPath();
    ctx.strokeStyle = '#27272a';
    ctx.moveTo(padL, y);
    ctx.lineTo(padL + plotW, y);
    ctx.stroke();
    ctx.fillStyle = '#d4d4d8';
    ctx.textAlign = 'right';
    ctx.fillText(formatMine(v + (basis.isPlan ? basis.center.north : basis.center.rl), 0), padL - 10, y + 4);
  }

  ctx.lineWidth = 2.2;
  ctx.lineCap = 'round';
  holes.forEach((hole) => {
    hole.segs.forEach((seg) => {
      ctx.strokeStyle = seg.color || '#eab308';
      ctx.beginPath();
      ctx.moveTo(toX(seg.a.u), mapY(seg.a.v));
      ctx.lineTo(toX(seg.b.u), mapY(seg.b.v));
      ctx.stroke();
    });
    const labelPt = hole.segs[0]?.a || hole.collar;
    if (labelPt) {
      ctx.fillStyle = '#facc15';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(hole.label, toX(labelPt.u) + 4, mapY(labelPt.v) - 4);
    }
  });

  ctx.fillStyle = '#facc15';
  ctx.font = 'bold 18px sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(title.site, padL, 28);
  ctx.fillStyle = '#e5e5e5';
  ctx.font = '13px sans-serif';
  ctx.fillText(title.line2, padL, 50);
  ctx.fillStyle = '#a1a1aa';
  ctx.font = '11px sans-serif';
  ctx.fillText(`${holes.length} hole${holes.length === 1 ? '' : 's'} on section  ·  ${new Date().toLocaleString()}`, padL, height - 24);

  ctx.save();
  ctx.translate(24, padT + plotH / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillStyle = '#a1a1aa';
  ctx.font = '12px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(basis.yLabel, 0, 0);
  ctx.restore();
  ctx.fillStyle = '#a1a1aa';
  ctx.textAlign = 'center';
  ctx.fillText(basis.xLabel, padL + plotW / 2, height - 48);

  const legend = gradeLegendRows(palette);
  let ly = padT;
  ctx.textAlign = 'left';
  ctx.font = '11px sans-serif';
  ctx.fillStyle = '#facc15';
  ctx.fillText(intervalMode === 'lith' ? 'Lithology' : 'Au g/t', width - padR + 16, ly);
  ly += 18;
  if (intervalMode !== 'lith') {
    legend.forEach((row) => {
      ctx.fillStyle = row.css;
      ctx.fillRect(width - padR + 16, ly - 10, 14, 12);
      ctx.fillStyle = '#e5e5e5';
      ctx.fillText(row.label, width - padR + 36, ly);
      ly += 18;
    });
  }

  drawNorth(ctx, width - 36, 36, 1);

  canvas._plotMeta = { holes, basis, title };
  return canvas;
}

export async function downloadSectionPlot(state, { projectName, format = 'png', thresholds, intervalMode, desurvey } = {}) {
  const canvas = renderSectionPlotCanvas(state, { projectName, thresholds, intervalMode, desurvey });
  const base = String(projectName || 'section').replace(/\s+/g, '-');
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  if (format === 'pdf') {
    const jpeg = await canvasToJpegBytes(canvas, 0.92);
    const pdf = jpegToPdfBytes(jpeg, canvas.width, canvas.height);
    downloadBlob(new Blob([pdf], { type: 'application/pdf' }), `${base}-section-${stamp}.pdf`);
    return canvas._plotMeta;
  }
  await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Could not encode PNG'));
        return;
      }
      downloadBlob(blob, `${base}-section-${stamp}.png`);
      resolve();
    }, 'image/png');
  });
  return canvas._plotMeta;
}
